import { getTheme, Icon, Link, Stack, Text } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, type Resolver, useForm } from "react-hook-form";

import {
  chipReplacementValueInCents,
  dataBonusValuePerGbInCents,
  lineAdditionalServiceCategoryLabels,
  lineAdditionalServiceFormDefaultValues,
  lineAdditionalServicePaymentMethodOptions
} from "../../constants";
import {
  LineAdditionalServiceCategory,
  LineAdditionalServiceType,
  LineAdditionalServiceUnit
} from "../../interfaces";
import type {
  LineAdditionalServicesNotes,
  LineAdditionalServicesNotesFormValues,
  LineAdditionalServiceFormValues
} from "../../interfaces";
import { lineAdditionalServiceFormSchema } from "../../schema/LineAdditionalServiceForm.schema";
import { LineAdditionalServiceSummary } from "../LineAdditionalServicesList/LineAdditionalServicesSummary";

import { LineAdditionalServiceDataBonusForm } from "./LineAdditionalServiceDataBonusForm";
import { LineAdditionalServiceDetailsForm } from "./LineAdditionalServiceDetailsForm";
import { LineAdditionalServiceNotesForm } from "./LineAdditionalServiceNotesForm";
import { LineAdditionalServicePaymentMethodForm } from "./LineAdditionalServicePaymentMethodForm";

import { PaymentMethodType } from "@/common/types/PaymentMethod.types";
import { currencyValueToLocaleString } from "@/utils/currencyValueToLocaleString";

interface FormValues {
  type: LineAdditionalServiceType;
  isChargeExempt: boolean;
  dataBonusGb: number;
  paymentMethod: PaymentMethodType.INVOICE_DEBIT | PaymentMethodType.PIX;
  notes: LineAdditionalServicesNotesFormValues;
}

interface NotesNormalizationStrategies {
  [LineAdditionalServiceType.DATA_BONUS]: () => LineAdditionalServicesNotes;
  [LineAdditionalServiceType.PHYSICAL_CHIP]: () => LineAdditionalServicesNotes;
  [LineAdditionalServiceType.ESIM]: () => LineAdditionalServicesNotes;
}

interface LineAdditionalServiceFormProps {
  allowedServiceCategories: Array<LineAdditionalServiceCategory>;
  isSubmitting: boolean;
  formId: string;
  onSubmit: (values: LineAdditionalServiceFormValues) => void;
  onCancel: () => void;
}

export function LineAdditionalServiceForm({
  allowedServiceCategories,
  isSubmitting,
  formId,
  onSubmit,
  onCancel
}: LineAdditionalServiceFormProps): JSX.Element {
  const theme = getTheme();
  const form = useForm<FormValues>({
    resolver: yupResolver(
      lineAdditionalServiceFormSchema
    ) as unknown as Resolver<FormValues>,
    defaultValues: lineAdditionalServiceFormDefaultValues
  });
  const { handleSubmit, setValue, watch } = form;
  const selectedServiceType = watch("type");
  const isChargeExempt = watch("isChargeExempt");
  const selectedDataBonusGb = watch("dataBonusGb");
  const paymentMethod = watch("paymentMethod");

  const selectedServiceCategory =
    selectedServiceType === LineAdditionalServiceType.DATA_BONUS
      ? LineAdditionalServiceCategory.DATA_BONUS
      : LineAdditionalServiceCategory.ACTIVATION_C;
  const isActivationCSelected =
    selectedServiceCategory === LineAdditionalServiceCategory.ACTIVATION_C;

  const serviceValueInCents = isActivationCSelected
    ? chipReplacementValueInCents
    : selectedDataBonusGb * dataBonusValuePerGbInCents;

  const valueInCents = isChargeExempt ? 0 : serviceValueInCents;

  const quantity = isActivationCSelected ? 1 : selectedDataBonusGb;

  const unit = isActivationCSelected
    ? LineAdditionalServiceUnit.UNIT
    : LineAdditionalServiceUnit.GB;

  const quantityLabel = isActivationCSelected
    ? "1 unidade"
    : `${selectedDataBonusGb} GB`;

  const paymentMethodBillingLabel =
    lineAdditionalServicePaymentMethodOptions.find(
      option => option.key === paymentMethod
    ).text;

  const paymentLabel = isChargeExempt
    ? "Sem cobrança"
    : paymentMethodBillingLabel;

  const valueLabel = isChargeExempt
    ? "Isento"
    : currencyValueToLocaleString(valueInCents / 100);

  const handleFormSubmit = (values: FormValues) => {
    const { details } = values.notes;
    const normalizedDetails = details?.trim();

    const detailsNotes = normalizedDetails
      ? { details: normalizedDetails }
      : {};

    const notesNormalizationStrategies: NotesNormalizationStrategies = {
      [LineAdditionalServiceType.DATA_BONUS]: () => detailsNotes,
      [LineAdditionalServiceType.PHYSICAL_CHIP]: () => ({
        ...detailsNotes,
        deliveryAddress: {
          ...values.notes.deliveryAddress,
          zipCode: values.notes.deliveryAddress.zipCode.replace(/\D/g, "")
        }
      }),
      [LineAdditionalServiceType.ESIM]: () => ({
        ...detailsNotes,
        eSimInfo: values.notes.eSimInfo
      })
    };

    const normalizeNotes = notesNormalizationStrategies[values.type];
    const normalizedNotes = normalizeNotes();

    const serviceValues: LineAdditionalServiceFormValues = {
      type: values.type,
      quantity,
      unit,
      value: valueInCents
    };

    if (!isChargeExempt) {
      serviceValues.paymentMethod = values.paymentMethod;
    }

    serviceValues.notes = normalizedNotes;

    onSubmit(serviceValues);
  };

  const handleServiceCategoryChange = (
    serviceCategory: LineAdditionalServiceCategory
  ) => {
    if (serviceCategory === LineAdditionalServiceCategory.DATA_BONUS) {
      setValue("type", LineAdditionalServiceType.DATA_BONUS);

      return;
    }

    if (selectedServiceType === LineAdditionalServiceType.DATA_BONUS) {
      setValue("type", LineAdditionalServiceType.PHYSICAL_CHIP);
    }
  };

  const renderServiceCategoryButton = (
    serviceCategory: LineAdditionalServiceCategory
  ) => {
    const isSelected = serviceCategory === selectedServiceCategory;
    const selectedColor = theme.palette.themeLighterAlt;
    const unselectedColor = theme.palette.white;
    const selectedBorderColor = theme.palette.themePrimary;
    const unselectedBorderColor = theme.palette.neutralLight;

    return (
      <button
        type="button"
        onClick={() => handleServiceCategoryChange(serviceCategory)}
        style={{
          background: isSelected ? selectedColor : unselectedColor,
          border: `1px solid ${
            isSelected ? selectedBorderColor : unselectedBorderColor
          }`,
          borderRadius: 8,
          cursor: "pointer",
          padding: 12,
          textAlign: "left",
          width: "100%"
        }}
      >
        <Text styles={{ root: { fontWeight: 600 } }}>
          {lineAdditionalServiceCategoryLabels[serviceCategory]}
        </Text>
      </button>
    );
  };

  return (
    <section
      style={{
        border: `1px solid ${theme.palette.neutralLight}`,
        borderRadius: 6,
        padding: 14
      }}
    >
      <FormProvider {...form}>
        <form
          id={formId}
          onSubmit={event => void handleSubmit(handleFormSubmit)(event)}
        >
          <Stack tokens={{ childrenGap: 14 }}>
            <Stack tokens={{ childrenGap: 4 }}>
              <Link
                href="#"
                onClick={event => {
                  event.preventDefault();

                  if (!isSubmitting) onCancel();
                }}
                disabled={isSubmitting}
                styles={{
                  root: {
                    alignItems: "center",
                    display: "inline-flex",
                    gap: 4,
                    textDecoration: "none",
                    width: "fit-content"
                  }
                }}
              >
                <Icon
                  iconName="ChevronLeft"
                  styles={{
                    root: {
                      fontSize: 8,
                      fontWeight: 600,
                      marginTop: 2
                    }
                  }}
                />
                <span className="ms-fontSize-12">Voltar</span>
              </Link>

              <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                Solicitar novo serviço adicional da linha
              </Text>
            </Stack>

            <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
              <Stack.Item
                grow
                styles={{ root: { flexBasis: 520, minWidth: 0 } }}
              >
                <Stack tokens={{ childrenGap: 10 }}>
                  <Stack horizontal wrap tokens={{ childrenGap: 10 }}>
                    {allowedServiceCategories.map(serviceCategory => (
                      <Stack.Item
                        key={serviceCategory}
                        grow
                        styles={{ root: { flexBasis: 240, minWidth: 0 } }}
                      >
                        {renderServiceCategoryButton(serviceCategory)}
                      </Stack.Item>
                    ))}
                  </Stack>

                  {selectedServiceType ===
                    LineAdditionalServiceType.DATA_BONUS && (
                    <LineAdditionalServiceDataBonusForm />
                  )}

                  {isActivationCSelected && <LineAdditionalServiceNotesForm />}

                  <LineAdditionalServicePaymentMethodForm />

                  <LineAdditionalServiceDetailsForm />
                </Stack>
              </Stack.Item>

              <Stack.Item
                styles={{
                  root: {
                    flexShrink: 0,
                    width: 320,
                    selectors: {
                      "@media(max-width: 760px)": {
                        width: "100%"
                      }
                    }
                  }
                }}
              >
                <LineAdditionalServiceSummary
                  serviceType={selectedServiceType}
                  quantityLabel={quantityLabel}
                  paymentLabel={paymentLabel}
                  valueLabel={valueLabel}
                  valueInCents={valueInCents}
                />
              </Stack.Item>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </section>
  );
}
